import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Users,
  FileText,
  Download,
  Filter,
  Eye,
  ThumbsUp,
  ThumbsDown,
  Plus,
  Paperclip,
  BarChart3,
  Umbrella
} from 'lucide-react';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';

const LeavesPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [activeSection, setActiveSection] = useState('leaves'); // leaves, reports

  // Mock Data
  const [stats, setStats] = useState({
    totalLeaves: 45,
    pending: 12,
    approved: 28,
    rejected: 5,
    remainingBalance: 120
  });

  const [leaves, setLeaves] = useState([]);

  const leaveTypes = [
    { id: 'annual', label: 'إجازة سنوية', color: 'blue' },
    { id: 'sick', label: 'إجازة مرضية', color: 'red' },
    { id: 'emergency', label: 'إجازة طارئة', color: 'orange' },
    { id: 'unpaid', label: 'بدون راتب', color: 'gray' },
    { id: 'rest', label: 'بدل راحة', color: 'green' },
    { id: 'special', label: 'إجازة خاصة', color: 'purple' }
  ];

  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      // Mock leaves data
      setLeaves([
        {
          id: 1,
          employee: 'أحمد محمد العتيبي',
          employeeId: 'EMP-001',
          type: 'annual',
          startDate: '2024-12-10',
          endDate: '2024-12-17',
          duration: 7,
          balanceAfter: 15,
          status: 'pending',
          reason: 'إجازة عائلية',
          attachments: [],
          submittedDate: '2024-12-05 10:30',
          approver: null,
          approverComment: null
        },
        {
          id: 2,
          employee: 'فاطمة سعيد الغامدي',
          employeeId: 'EMP-002',
          type: 'sick',
          startDate: '2024-12-06',
          endDate: '2024-12-08',
          duration: 3,
          balanceAfter: 12,
          status: 'approved',
          reason: 'وعكة صحية',
          attachments: ['تقرير_طبي.pdf'],
          submittedDate: '2024-12-05 08:00',
          approver: 'سعود المطيري',
          approvedDate: '2024-12-05 09:00',
          approverComment: 'معتمد - تمنياتنا بالشفاء العاجل'
        },
        {
          id: 3,
          employee: 'خالد عبدالله الشمري',
          employeeId: 'EMP-003',
          type: 'annual',
          startDate: '2024-11-20',
          endDate: '2024-11-24',
          duration: 5,
          balanceAfter: 17,
          status: 'approved',
          reason: 'سفر عائلي',
          attachments: [],
          submittedDate: '2024-11-10 14:20',
          approver: 'سعود المطيري',
          approvedDate: '2024-11-11 08:30',
          approverComment: 'معتمد'
        },
        {
          id: 4,
          employee: 'نورة حسن القحطاني',
          employeeId: 'EMP-004',
          type: 'emergency',
          startDate: '2024-12-07',
          endDate: '2024-12-07',
          duration: 1,
          balanceAfter: 3,
          status: 'rejected',
          reason: 'ظروف شخصية',
          attachments: [],
          submittedDate: '2024-12-06 16:00',
          approver: 'سعود المطيري',
          approvedDate: '2024-12-06 16:30',
          approverComment: 'مرفوض - لا يوجد مبرر كافٍ'
        },
        {
          id: 5,
          employee: 'سعود إبراهيم المطيري',
          employeeId: 'EMP-005',
          type: 'rest',
          startDate: '2024-12-09',
          endDate: '2024-12-11',
          duration: 3,
          balanceAfter: 8,
          status: 'approved',
          reason: 'بدل عمل إضافي',
          attachments: [],
          submittedDate: '2024-12-04 11:00',
          approver: 'الإدارة العليا',
          approvedDate: '2024-12-04 13:00',
          approverComment: 'معتمد - بدل عمل إضافي شهر نوفمبر'
        },
        {
          id: 6,
          employee: 'منى عبدالرحمن الدوسري',
          employeeId: 'EMP-006',
          type: 'unpaid',
          startDate: '2024-12-15',
          endDate: '2024-12-20',
          duration: 6,
          balanceAfter: 0,
          status: 'pending',
          reason: 'ظروف عائلية طارئة',
          attachments: ['خطاب_رسمي.pdf'],
          submittedDate: '2024-12-05 15:00',
          approver: null,
          approverComment: null
        }
      ]);
      setLoading(false);
    }, 500);
  }, []);

  const getLeaveTypeBadge = (typeId) => {
    const type = leaveTypes.find(t => t.id === typeId);
    const colors = {
      blue: 'bg-blue-100 text-blue-700 border-blue-300',
      red: 'bg-red-100 text-red-700 border-red-300',
      orange: 'bg-orange-100 text-orange-700 border-orange-300',
      gray: 'bg-gray-100 text-gray-700 border-gray-300',
      green: 'bg-green-100 text-green-700 border-green-300',
      purple: 'bg-purple-100 text-purple-700 border-purple-300'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${colors[type.color]}`}>
        {type.label}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    const statuses = {
      pending: { label: 'بانتظار الموافقة', color: 'bg-yellow-100 text-yellow-700 border-yellow-300' },
      approved: { label: 'معتمد', color: 'bg-green-100 text-green-700 border-green-300' },
      rejected: { label: 'مرفوض', color: 'bg-red-100 text-red-700 border-red-300' }
    };
    const statusInfo = statuses[status];
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  const filteredLeaves = leaves.filter(leave => {
    const matchesType = filterType === 'all' || leave.type === filterType;
    const matchesStatus = filterStatus === 'all' || leave.status === filterStatus;
    return matchesType && matchesStatus;
  });

  const handleApprove = (leaveId) => {
    alert(`اعتماد طلب الإجازة رقم ${leaveId}`);
  };

  const handleReject = (leaveId) => {
    alert(`رفض طلب الإجازة رقم ${leaveId}`);
  };

  const handleViewDetails = (leave) => {
    alert(`عرض تفاصيل طلب: ${leave.employee}`);
  };

  const handleExport = () => {
    alert('تصدير سجل الإجازات إلى Excel');
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">جاري تحميل بيانات الإجازات...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!hasPermission('hr.leave.view')) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-600">ليس لديك صلاحية الوصول لنظام الإجازات</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-black text-[#1E3A8A] mb-2 flex items-center gap-3">
                <Umbrella className="w-8 h-8" />
                <span>نظام الإجازات</span>
              </h1>
              <p className="text-gray-600">إدارة شاملة لطلبات الإجازات والرصيد</p>
            </div>

            <div className="flex items-center gap-3">
              {hasPermission('hr.leave.create') && (
                <button
                  onClick={() => alert('تقديم طلب إجازة جديد')}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#1E3A8A] text-white rounded-lg font-bold hover:bg-[#152d6e] transition-all"
                >
                  <Plus className="w-4 h-4" />
                  <span>طلب إجازة</span>
                </button>
              )}
              {hasPermission('hr.leave.report') && (
                <button
                  onClick={handleExport}
                  className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#C8A870] text-[#C8A870] rounded-lg font-bold hover:bg-[#C8A870] hover:text-white transition-all"
                >
                  <Download className="w-4 h-4" />
                  <span>تصدير</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Stats Cards - KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          {/* Total Leaves */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-[#1E3A8A] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">إجمالي الإجازات</p>
                <p className="text-3xl font-black text-[#1E3A8A]">{stats.totalLeaves}</p>
              </div>
              <Calendar className="w-12 h-12 text-[#1E3A8A] opacity-20" />
            </div>
          </div>

          {/* Pending */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-yellow-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">المعلقة</p>
                <p className="text-3xl font-black text-yellow-600">{stats.pending}</p>
              </div>
              <Clock className="w-12 h-12 text-yellow-600 opacity-20" />
            </div>
          </div>

          {/* Approved */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-green-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">المعتمدة</p>
                <p className="text-3xl font-black text-green-600">{stats.approved}</p>
              </div>
              <CheckCircle className="w-12 h-12 text-green-600 opacity-20" />
            </div>
          </div>

          {/* Rejected */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-red-200 p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">المرفوضة</p>
                <p className="text-3xl font-black text-red-600">{stats.rejected}</p>
              </div>
              <XCircle className="w-12 h-12 text-red-600 opacity-20" />
            </div>
          </div>

          {/* Remaining Balance */}
          <div className="bg-white rounded-lg shadow-sm border-2 border-[#C8A870] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">الرصيد المتبقي</p>
                <p className="text-3xl font-black text-[#C8A870]">{stats.remainingBalance}</p>
              </div>
              <AlertCircle className="w-12 h-12 text-[#C8A870] opacity-20" />
            </div>
          </div>
        </div>

        {/* Leave Types Legend */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-4 mb-6">
          <h3 className="text-sm font-bold text-gray-700 mb-3">أنواع الإجازات</h3>
          <div className="flex flex-wrap gap-3">
            {leaveTypes.map((type) => (
              <div key={type.id} className="flex items-center gap-2">
                {getLeaveTypeBadge(type.id)}
              </div>
            ))}
          </div>
        </div>

        {/* Section Tabs */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 mb-6">
          <div className="flex border-b-2 border-gray-200">
            <button
              onClick={() => setActiveSection('leaves')}
              className={`flex items-center gap-2 px-6 py-4 font-bold transition-all ${
                activeSection === 'leaves'
                  ? 'border-b-4 border-[#1E3A8A] text-[#1E3A8A] bg-blue-50'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <Calendar className="w-5 h-5" />
              سجل الإجازات ({leaves.filter(l => l.status === 'pending').length})
            </button>
            <button
              onClick={() => setActiveSection('reports')}
              className={`flex items-center gap-2 px-6 py-4 font-bold transition-all ${
                activeSection === 'reports'
                  ? 'border-b-4 border-[#1E3A8A] text-[#1E3A8A] bg-blue-50'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <BarChart3 className="w-5 h-5" />
              التقارير
            </button>
          </div>

          <div className="p-6">
            {/* Leaves Section */}
            {activeSection === 'leaves' && (
              <div>
                {/* Filters */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="flex items-center gap-2">
                    <Filter className="w-5 h-5 text-gray-600" />
                    <select
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none font-semibold"
                    >
                      <option value="all">جميع الأنواع</option>
                      {leaveTypes.map((type) => (
                        <option key={type.id} value={type.id}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center gap-2">
                    <Filter className="w-5 h-5 text-gray-600" />
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none font-semibold"
                    >
                      <option value="all">جميع الحالات</option>
                      <option value="pending">معلقة</option>
                      <option value="approved">معتمدة</option>
                      <option value="rejected">مرفوضة</option>
                    </select>
                  </div>
                </div>

                {/* Leaves List */}
                {filteredLeaves.length > 0 ? (
                  <div className="space-y-4">
                    {filteredLeaves.map((leave) => (
                      <div
                        key={leave.id}
                        className={`border-2 rounded-lg p-4 ${
                          leave.status === 'pending'
                            ? 'border-yellow-200 bg-yellow-50'
                            : leave.status === 'approved'
                            ? 'border-green-200 bg-green-50'
                            : 'border-red-200 bg-red-50'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              {getLeaveTypeBadge(leave.type)}
                              {getStatusBadge(leave.status)}
                            </div>
                            <h4 className="font-bold text-gray-800 text-lg mb-1">{leave.employee}</h4>
                            <p className="text-sm text-gray-600">
                              {leave.employeeId} • تاريخ التقديم: {leave.submittedDate}
                            </p>
                          </div>
                          <button
                            onClick={() => handleViewDetails(leave)}
                            className="p-2 text-[#1E3A8A] hover:bg-blue-100 rounded-lg transition-colors"
                            title="عرض التفاصيل"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                        </div>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                          <div className="p-3 bg-white rounded-lg">
                            <p className="text-xs text-gray-600 mb-1">من</p>
                            <p className="font-bold text-gray-800">{leave.startDate}</p>
                          </div>
                          <div className="p-3 bg-white rounded-lg">
                            <p className="text-xs text-gray-600 mb-1">إلى</p>
                            <p className="font-bold text-gray-800">{leave.endDate}</p>
                          </div>
                          <div className="p-3 bg-white rounded-lg">
                            <p className="text-xs text-gray-600 mb-1">المدة</p>
                            <p className="font-bold text-[#1E3A8A]">{leave.duration} {leave.duration === 1 ? 'يوم' : 'أيام'}</p>
                          </div>
                          <div className="p-3 bg-white rounded-lg">
                            <p className="text-xs text-gray-600 mb-1">الرصيد بعد الطلب</p>
                            <p className="font-bold text-[#C8A870]">{leave.balanceAfter} {leave.balanceAfter === 1 ? 'يوم' : 'أيام'}</p>
                          </div>
                        </div>

                        <div className="mb-4 p-3 bg-white rounded-lg">
                          <p className="text-xs text-gray-600 mb-1">السبب</p>
                          <p className="text-sm text-gray-800">{leave.reason}</p>
                        </div>

                        {leave.attachments && leave.attachments.length > 0 && (
                          <div className="mb-4 p-3 bg-white rounded-lg">
                            <p className="text-xs text-gray-600 mb-2">المرفقات</p>
                            <div className="flex flex-wrap gap-2">
                              {leave.attachments.map((file, idx) => (
                                <div key={idx} className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-lg text-sm">
                                  <Paperclip className="w-4 h-4 text-gray-600" />
                                  <span>{file}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {leave.status !== 'pending' && (
                          <div className="mb-4 p-3 bg-white rounded-lg">
                            <p className="text-xs text-gray-600 mb-1">
                              {leave.status === 'approved' ? 'معتمد من' : 'مرفوض من'}
                            </p>
                            <p className="text-sm font-bold text-gray-800 mb-1">
                              {leave.approver} • {leave.approvedDate}
                            </p>
                            {leave.approverComment && (
                              <div className="mt-2 p-2 bg-gray-50 rounded">
                                <p className="text-xs text-gray-600">تعليق الإدارة:</p>
                                <p className="text-sm text-gray-800">{leave.approverComment}</p>
                              </div>
                            )}
                          </div>
                        )}

                        {leave.status === 'pending' && hasPermission('hr.leave.approve') && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleApprove(leave.id)}
                              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition-all"
                            >
                              <ThumbsUp className="w-4 h-4" />
                              اعتماد
                            </button>
                            <button
                              onClick={() => handleReject(leave.id)}
                              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 transition-all"
                            >
                              <ThumbsDown className="w-4 h-4" />
                              رفض
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Calendar className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>لا توجد إجازات</p>
                  </div>
                )}
              </div>
            )}

            {/* Reports Section */}
            {activeSection === 'reports' && (
              <div>
                <h3 className="text-lg font-black text-gray-800 mb-4">التقارير</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <button
                    onClick={() => alert('تقرير أخذ الإجازات')}
                    className="p-6 bg-white border-2 border-gray-200 rounded-lg hover:border-[#1E3A8A] transition-all text-right"
                  >
                    <Calendar className="w-8 h-8 text-[#1E3A8A] mb-3" />
                    <h4 className="font-bold text-gray-800 mb-1">تقرير أخذ الإجازات</h4>
                    <p className="text-sm text-gray-600">عرض جميع الإجازات المأخوذة</p>
                  </button>

                  <button
                    onClick={() => alert('تقرير الرصيد المتبقي')}
                    className="p-6 bg-white border-2 border-gray-200 rounded-lg hover:border-[#1E3A8A] transition-all text-right"
                  >
                    <AlertCircle className="w-8 h-8 text-[#C8A870] mb-3" />
                    <h4 className="font-bold text-gray-800 mb-1">تقرير الرصيد المتبقي</h4>
                    <p className="text-sm text-gray-600">عرض رصيد الإجازات لكل موظف</p>
                  </button>

                  <button
                    onClick={() => alert('تقرير كثافة الغياب')}
                    className="p-6 bg-white border-2 border-gray-200 rounded-lg hover:border-[#1E3A8A] transition-all text-right"
                  >
                    <Users className="w-8 h-8 text-red-600 mb-3" />
                    <h4 className="font-bold text-gray-800 mb-1">تقرير كثافة الغياب</h4>
                    <p className="text-sm text-gray-600">تحليل فترات الغياب</p>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default LeavesPage;
